import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiFacebook, FiTwitter, FiInstagram, FiLinkedin, FiMail, FiPhone, FiMapPin } from 'react-icons/fi';

/**
 * Footer Component
 * Props:
 * - contactInfo {address, phone, email}
 * - accreditationLogos [{iconUrl, title}]
 * - socialLinks [{platform, url}]
 * Features:
 * - Grid layout: Contact | Sitemap | Accreditations | Social
 * - Animations: fade-in per section
 * - Reusable SocialIcon & AccreditationLogo components
 * - Accessibility: focusable links
 */

const SocialIcon = ({ platform, url }) => {
  const icons = {
    facebook: FiFacebook,
    twitter: FiTwitter,
    instagram: FiInstagram,
    linkedin: FiLinkedin,
  };

  const Icon = icons[platform.toLowerCase()];

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-gray-400 hover:text-secondary-500 transition-colors duration-300 hover:scale-110 transform"
      aria-label={`Visit our ${platform} page`}
    >
      {Icon && <Icon className="text-2xl" />}
    </a>
  );
};

const AccreditationLogo = ({ iconUrl, title }) => (
  <div className="flex flex-col items-center">
    <img src={iconUrl} alt={title} className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
    <span className="text-xs text-gray-500 mt-2">{title}</span>
  </div>
);

const Footer = ({
  contactInfo = {
    address: '123 Security Street, London, UK',
    phone: '+44 20 1234 5678',
    email: 'info@secureguard.com',
  },
  socialLinks = [
    { platform: 'facebook', url: 'https://facebook.com' },
    { platform: 'twitter', url: 'https://twitter.com' },
    { platform: 'instagram', url: 'https://instagram.com' },
    { platform: 'linkedin', url: 'https://linkedin.com' },
  ],
  accreditationLogos = [
    { iconUrl: '/accreditation1.png', title: 'ISO 9001' },
    { iconUrl: '/accreditation2.png', title: 'NSI Gold' },
    { iconUrl: '/accreditation3.png', title: 'SSAIB' },
  ],
}) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <footer className="bg-gray-900 text-white pt-16 pb-6">
      <motion.div
        className="container-custom"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Contact Section */}
          <motion.div variants={itemVariants}>
            <h3 className="text-lg font-semibold mb-4 uppercase tracking-wide text-accent-500">Contact Us</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <FiMapPin className="text-secondary-400 mt-1 flex-shrink-0" />
                <span className="text-sm text-gray-300">{contactInfo.address}</span>
              </div>
              <div className="flex items-center space-x-3">
                <FiPhone className="text-secondary-400 flex-shrink-0" />
                <a
                  href={`tel:${contactInfo.phone}`}
                  className="text-sm text-gray-300 hover:text-secondary-400 transition-colors duration-300"
                >
                  {contactInfo.phone}
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <FiMail className="text-secondary-400 flex-shrink-0" />
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="text-sm text-gray-300 hover:text-secondary-400 transition-colors duration-300"
                >
                  {contactInfo.email}
                </a>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={itemVariants}>
            <h3 className="text-lg font-semibold mb-4 uppercase tracking-wide text-accent-500">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/products" className="text-sm text-gray-300 hover:text-secondary-400 transition-colors duration-300">
                  Products
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-sm text-gray-300 hover:text-secondary-400 transition-colors duration-300">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/b2b" className="text-sm text-gray-300 hover:text-secondary-400 transition-colors duration-300">
                  B2B Portal
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm text-gray-300 hover:text-secondary-400 transition-colors duration-300">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-gray-300 hover:text-secondary-400 transition-colors duration-300">
                  Contact
                </Link>
              </li>
            </ul>
          </motion.div>

          {/* Accreditations */}
          <motion.div variants={itemVariants}>
            <h3 className="text-lg font-semibold mb-4 uppercase tracking-wide text-accent-500">Accreditations</h3>
            <div className="flex flex-wrap gap-4">
              {accreditationLogos.map((logo, index) => (
                <AccreditationLogo key={index} {...logo} />
              ))}
            </div>
          </motion.div>

          {/* Social Media */}
          <motion.div variants={itemVariants}>
            <h3 className="text-lg font-semibold mb-4 uppercase tracking-wide text-accent-500">Follow Us</h3>
            <div className="flex space-x-4">
              {socialLinks.map((link, index) => (
                <SocialIcon key={index} {...link} />
              ))}
            </div>
            <div className="mt-6">
              <h4 className="text-sm font-semibold mb-2">Newsletter</h4>
              <form className="flex flex-col space-y-2">
                <input
                  type="email"
                  placeholder="Your email"
                  className="px-4 py-2 rounded-lg bg-primary-700 border border-primary-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                  aria-label="Email for newsletter"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors duration-300"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-6 mt-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} SecureGuard. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-sm text-gray-400 hover:text-secondary-400 transition-colors duration-300">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-gray-400 hover:text-secondary-400 transition-colors duration-300">
                Terms of Service
              </Link>
              <Link to="/cookies" className="text-sm text-gray-400 hover:text-secondary-400 transition-colors duration-300">
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </footer>
  );
};

export default Footer;

